import React, { useCallback, useEffect, useState } from "react";
import { PlusCircle, ChevronRight, X, Shield, Database, KeyRound, Instagram } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useAxiosInstance } from "../ConversionReportPage/components/axiosInstance";
import { DatePickerWithRange } from "@/components/dashboard_component/DatePickerWithRange";
import Loader from "@/components/dashboard_component/loader";
import { useSelector } from "react-redux";
import type { RootState } from "@/store";
import { FacebookLogo, GoogleLogo, Ga4Logo, ShopifyLogo } from "@/data/logo";
import { useNavigate } from "react-router-dom";
import PlatformModal from "@/components/dashboard_component/PlatformModal";
import TicketForm from "@/components/dashboard_component/TicketForm";
import ConversionFunnelCard from "./components/ConversionFunnelCard";
import MarketingInsightsCard from "./components/MarketingInsightsCard";
import PerformanceTable from "./components/PerformanceTable";
import { Platform, PerformanceSummary } from "./components/PerformanceTable";
import PaymentOrdersCard from "./components/PaymentOrdersCard";
import InstagramPerformanceTable from "./components/InstagramPerformanceTable";
import { useBrandRefresh } from "@/hooks/useBrandRefresh";
import ReportTable, {
  BreakdownCatagory,
  BreakdownResponse,
} from "./components/ReportTable";

// Component to display a card for connecting platforms
export function ConnectPlatformCard({
  platform,
  onClick,
}: {
  platform: Platform;
  onClick: () => void;
}) {
  const getPlatformLogo = () => {
    switch (platform) {
      case "Facebook":
        return <FacebookLogo width={"2rem"} height={"2rem"} />;
      case "Google Ads":
        return <GoogleLogo width={"2rem"} height={"2rem"} />;
      case "Google Analytics":
        return <Ga4Logo width={"2rem"} height={"2rem"} />;
      case "Shopify":
        return <ShopifyLogo width={"2rem"} height={"2rem"} />;
      case "Instagram":
        return <Instagram size={32} className="text-pink-600" />;
      default:
        return null;
    }
  };

  return (
    <div
      className="flex flex-col items-center justify-center p-6 border rounded-lg bg-white shadow-sm hover:shadow-md transition-shadow duration-200 cursor-pointer"
      onClick={onClick}
    >
      <div className="mb-3">{getPlatformLogo()}</div>
      <h3 className="text-lg font-semibold text-slate-800 mb-2">
        Connect {platform}
      </h3>
      <p className="text-sm text-slate-500 text-center mb-4">
        Get better insights by connecting your {platform} account
      </p>
      <Button variant="outline" className="gap-2">
        <PlusCircle className="h-4 w-4" />
        Connect
      </Button>
    </div>
  );
}

// Generic Dashboard Card Component
interface DashboardCardProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  onNavigate: () => void;
  className?: string;
}

export function DashboardCard({
  title,
  icon,
  children,
  onNavigate,
  className,
}: DashboardCardProps) {
  return (
    <div
      className={cn(
        "bg-white border rounded-lg shadow-md hover:shadow-lg transition-all duration-300 cursor-pointer group flex flex-col h-[380px]",
        className,
      )}
      onClick={onNavigate}
    >
      <div className="p-4 border-b border-slate-100 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-blue-50 rounded-lg group-hover:bg-blue-100 transition-colors">
              {icon}
            </div>
            <h3 className="font-semibold text-slate-800">{title}</h3>
          </div>
          <ChevronRight className="h-5 w-5 text-slate-400 group-hover:text-blue-500 group-hover:translate-x-1 transition-all" />
        </div>
      </div>
      <div className="p-4 flex-1 overflow-y-auto">{children}</div>
    </div>
  );
}

// Main dashboard component
const SummaryDashboard: React.FC = () => {
  const user = useSelector((state: RootState) => state.user.user);
  const brandId = useSelector(
    (state: RootState) => state.brand.selectedBrandId,
  );
  const brands = useSelector((state: RootState) => state.brand.brands);
  const dateFrom = useSelector((state: RootState) => state.date.from);
  const dateTo = useSelector((state: RootState) => state.date.to);
  const compareFrom = useSelector((state: RootState) => state.date.compareFrom);
  const compareTo = useSelector((state: RootState) => state.date.compareTo);
  const selectedBrand = brands.find((brand) => brand._id === brandId);
  const isShopifyConnected =
    !!selectedBrand?.shopifyAccount &&
    (Boolean(selectedBrand.shopifyAccount.shopifyAccessToken) ||
      Boolean(selectedBrand.shopifyAccount.shopName));

  const isInstagramConnected = !!(selectedBrand?.igAccountIds && selectedBrand.igAccountIds.length > 0);

  const userName = user?.username;
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [performanceData, setPerformanceData] = useState<{
    meta?: PerformanceSummary["periodData"];
    google?: PerformanceSummary["periodData"];
    shopify?: PerformanceSummary["periodData"];
    analytics?: PerformanceSummary["periodData"];
  }>({});
  const [breakdownDim, setBreakdownDim] = useState<BreakdownCatagory>("age");
  const [breakdown, setBreakdown] = useState<BreakdownResponse>();
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const axiosInstance = useAxiosInstance();

  const navigate = useNavigate();
  const { refreshBrands } = useBrandRefresh();

  // Track API call success/failure status
  const [apiStatus, setApiStatus] = useState({
    meta: true,
    google: true,
    shopify: true,
    analytics: true,
  });

  // State for platform modal
  const [platformModalOpen, setPlatformModalOpen] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState<Platform | null>(
    null,
  );

  // State for banner visibility
  const [bannerVisible, setBannerVisible] = useState(true);

  // State for help desk modal
  const [helpDeskModalOpen, setHelpDeskModalOpen] = useState(false);

  // Check URL parameters for modal opening
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const modalToOpen = params.get("openModal");

    if (modalToOpen && brandId) {
      let platformToOpen: Platform | null = null;

      switch (modalToOpen.toLowerCase()) {
        case "googleads":
          platformToOpen = "Google Ads";
          break;
        case "googleanalytics":
          platformToOpen = "Google Analytics";
          break;
        case "facebook":
          platformToOpen = "Facebook";
          break;
      }

      if (platformToOpen) {
        setSelectedPlatform(platformToOpen);
        setPlatformModalOpen(true);

        // Remove the modal parameter from URL
        params.delete("openModal");
        const newUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ""}`;
        window.history.replaceState({}, "", newUrl);
      }
    }
  }, [brandId]);

  const fetchPerformanceData = useCallback(async () => {
    if (!brandId) {
      console.log("No brand ID available, skipping API calls");
      setInitialLoading(false);
      return;
    }

    setLoading(true);

    try {
      const dateParams =
        dateFrom && dateTo
          ? {
              customStart: dateFrom,
              customEnd: dateTo,
              ...(compareFrom && compareTo
                ? {
                    customCompareStart: compareFrom,
                    customCompareEnd: compareTo,
                  }
                : {}),
            }
          : {};

      const metaPromise = axiosInstance
        .get(`api/summary/facebook-ads/${brandId}`, {
          params: dateParams,
          withCredentials: true,
        })
        .catch((error) => {
          console.error("Error fetching Meta data:", error);
          const errData = error?.response?.data;
          // Only mark as disconnected if token expired / reconnect needed
          if (errData?.reconnectRequired) {
            setApiStatus((prev) => ({ ...prev, meta: false }));
          }
          return { data: { success: false, connected: errData?.connected, reconnectRequired: errData?.reconnectRequired } };
        });

      const googlePromise = axiosInstance
        .get(`api/summary/google-ads/${brandId}`, {
          params: dateParams,
          withCredentials: true,
        })
        .catch((error) => {
          console.error("Error fetching Google data:", error);
          const errData = error?.response?.data;
          if (errData?.reconnectRequired) {
            setApiStatus((prev) => ({ ...prev, google: false }));
          }
          return { data: { success: false, connected: errData?.connected, reconnectRequired: errData?.reconnectRequired } };
        });

      const shopifyPromise = axiosInstance
        .get(`api/summary/shopify/${brandId}`, {
          params: dateParams,
          withCredentials: true,
        })
        .catch((error) => {
          console.error("Error fetching Shopify data:", error);
          return { data: { success: false } };
        });

      const analyticsPromise = axiosInstance
        .get(`api/summary/analytics/${brandId}`, {
          params: dateParams,
          withCredentials: true,
        })
        .catch((error) => {
          console.error("Error fetching Analytics data:", error);
          const errData = error?.response?.data;
          if (errData?.reconnectRequired) {
            setApiStatus((prev) => ({ ...prev, analytics: false }));
          }
          return { data: { success: false, connected: errData?.connected, reconnectRequired: errData?.reconnectRequired } };
        });

      const [metaResponse, googleResponse, shopifyResponse, analyticsResponse] =
        await Promise.all([
          metaPromise,
          googlePromise,
          shopifyPromise,
          analyticsPromise,
        ]);

      setPerformanceData({
        meta: metaResponse.data.success
          ? metaResponse.data.periodData
          : undefined,
        google: googleResponse.data.success
          ? googleResponse.data.periodData
          : undefined,
        shopify: shopifyResponse.data.success
          ? shopifyResponse.data.periodData
          : undefined,
        analytics: analyticsResponse.data.success
          ? analyticsResponse.data.periodData
          : undefined,
      });

      // Helper: platform is disconnected only if server explicitly says
      // connected === false (not configured) or reconnectRequired === true (token expired)
      const isDisconnected = (res: any) =>
        res.data.connected === false || res.data.reconnectRequired === true;

      setApiStatus({
        meta: !isDisconnected(metaResponse),
        google: !isDisconnected(googleResponse),
        shopify: shopifyResponse.data.success,
        analytics: !isDisconnected(analyticsResponse),
      });
    } catch (error) {
      console.error("Error in fetchPerformanceData:", error);
      // Overall error does NOT mean platforms are disconnected — don't touch apiStatus
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [brandId, dateFrom, dateTo, compareFrom, compareTo, axiosInstance]);

  const fetchBreakdownData = useCallback(
    async (forceRefresh = false) => {
      if (!brandId) {
        setBreakdown(undefined);
        return;
      }

      setBreakdownLoading(true);

      try {
        const dateParams =
          dateFrom && dateTo
            ? {
                customStart: dateFrom,
                customEnd: dateTo,
                ...(compareFrom && compareTo
                  ? {
                      customCompareStart: compareFrom,
                      customCompareEnd: compareTo,
                    }
                  : {}),
              }
            : {};

        const response = await axiosInstance.get(
          `api/meta/report/breakdown/${brandId}`,
          {
            params: {
              breakdownCatagory: breakdownDim,
              ...dateParams,
              ...(forceRefresh ? { refresh: "true" } : {}),
            },
            withCredentials: true,
          },
        );

        setBreakdown(response.data.success ? response.data : undefined);
      } catch (error) {
        console.error("Error fetching Meta breakdown data:", error);
        setBreakdown(undefined);
      } finally {
        setBreakdownLoading(false);
      }
    },
    [
      brandId,
      breakdownDim,
      dateFrom,
      dateTo,
      compareFrom,
      compareTo,
      axiosInstance,
    ],
  );

  // Shopify connect-brand callback uses query params to report success/failure.
  // When present, close the modal, refresh brands, and clean up the URL.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const shopifyConnected = params.get("shopify_connected");
    const callbackBrandId = params.get("brandId");

    if (!shopifyConnected) return;
    if (callbackBrandId && brandId && callbackBrandId !== brandId) {
      // Still proceed to refresh brands, but don't assume currently selected brand.
    }

    (async () => {
      try {
        // Close any open PlatformModal (Shopify flow)
        setPlatformModalOpen(false);
        setSelectedPlatform(null);

        await refreshBrands();
        await fetchPerformanceData();
        await fetchBreakdownData();
      } finally {
        params.delete("shopify_connected");
        params.delete("brandId");
        params.delete("shop");
        params.delete("error");
        const newUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ""}`;
        window.history.replaceState({}, "", newUrl);
      }
    })();
  }, [brandId, refreshBrands, fetchPerformanceData, fetchBreakdownData]);

  const handleConnectPlatform = (platform: Platform) => {
    setSelectedPlatform(platform);
    setPlatformModalOpen(true);
  };

  const handlePlatformModalSuccess = (
    platform: string,
    accountName: string,
    accountId: string,
  ) => {
    console.log(
      `Successfully connected ${platform} - ${accountName} (${accountId})`,
    );
    fetchPerformanceData();
    fetchBreakdownData();
  };

  // Check if the selected date range is older than 37 months
  const isOver37Months = React.useMemo(() => {
    if (dateFrom) {
      const diffTime = new Date().getTime() - new Date(dateFrom).getTime();
      const diffMonths = diffTime / (1000 * 60 * 60 * 24 * 30.44);
      // Use 37.1 to safely account for time differences and leap years
      return diffMonths > 37.1;
    }
    return false;
  }, [dateFrom]);

  useEffect(() => {
    fetchPerformanceData();
  }, [fetchPerformanceData]);

  useEffect(() => {
    fetchBreakdownData();
  }, [fetchBreakdownData]);

  if (initialLoading) {
    return <Loader isLoading />;
  }

  // Check if any platform is not connected (Shopify optional for comparison)
  const allConnected =
    apiStatus.meta &&
    apiStatus.google &&
    apiStatus.analytics &&
    isShopifyConnected &&
    isInstagramConnected;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <div className="w-full border-b border-emerald-100 bg-emerald-50/80">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-8 gap-y-2 px-6 py-3 text-sm text-emerald-900">
          <div className="flex items-center gap-2">
            <Database className="h-4 w-4 shrink-0 text-emerald-600" />
            <span>No data is stored</span>
          </div>
          <div className="hidden h-4 w-px bg-emerald-200 sm:block" aria-hidden />
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4 shrink-0 text-emerald-600" />
            <span>No third party gets your data</span>
          </div>
          <div className="hidden h-4 w-px bg-emerald-200 sm:block" aria-hidden />
          <div className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 shrink-0 text-emerald-600" />
            <span>You can revoke access anytime</span>
          </div>
        </div>
      </div>

      <div className="mx-auto p-6 max-w-7xl">
        <div className="mb-6 animate-fade-up">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="h-12 w-1 bg-blue-500 rounded-full" />
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold tracking-tight">
                  Welcome Back, {userName}
                </h1>
                <p className="text-slate-500 mt-1">
                  Here's your performance overview
                </p>
              </div>
            </div>
            <div className="flex flex-row items-center gap-3">
              <DatePickerWithRange
                defaultDate={{
                  from: new Date(
                    new Date().getFullYear(),
                    new Date().getMonth(),
                    1,
                  ),
                  to: new Date(),
                }}
              />
              {/* <HeaderTutorialButton /> */}
            </div>
          </div>
        </div>

        {/* Requirements Banner */}
        {bannerVisible && (
          <div className="mb-6 animate-fade-up">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg shadow-lg p-6 relative overflow-hidden">
              <button
                onClick={() => setBannerVisible(false)}
                className="absolute top-4 right-4 text-white hover:text-blue-100 transition-colors"
                aria-label="Close banner"
              >
                <X className="h-5 w-5" />
              </button>
              <div className="flex items-start gap-4 pr-8">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold mb-2">
                    Have Custom Requirements?
                  </h3>
                  <p className="text-blue-50">
                    If you have any requirements, fill the form and we will try
                    to integrate in 15 days.
                  </p>
                </div>
                <Button
                  variant="secondary"
                  className="bg-white text-blue-600 hover:bg-blue-50 font-medium"
                  onClick={() => setHelpDeskModalOpen(true)}
                >
                  Fill Form
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* 37-Month Warning Banner */}
        {isOver37Months && (
          <div className="mb-6 animate-fade-up">
            <div className="bg-amber-50 border-l-4 border-amber-500 text-amber-900 rounded-lg shadow-sm p-4 relative overflow-hidden flex items-start gap-3">
              <Shield className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-semibold mb-1">
                  Data Range Notice
                </h3>
                <p className="text-sm text-amber-800">
                  You have selected a date older than 37 months. Meta limits historical data to 37 months, so your Meta metrics only reflect the last 37 months. Other platforms are showing your full selected date range.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Missing Platforms Section */}
        {!allConnected && (
          <div className="mb-8 animate-fade-up">
            <div className="bg-white border rounded-lg shadow-md p-6 space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-slate-800">
                  Connect Your Platforms
                </h2>
              </div>
              <p className="text-slate-600">
                Connect your advertising and analytics platforms to see all your
                performance data in one place.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {!apiStatus.meta && (
                  <ConnectPlatformCard
                    platform="Facebook"
                    onClick={() => handleConnectPlatform("Facebook")}
                  />
                )}
                {!apiStatus.google && (
                  <ConnectPlatformCard
                    platform="Google Ads"
                    onClick={() => handleConnectPlatform("Google Ads")}
                  />
                )}
                {!apiStatus.analytics && (
                  <ConnectPlatformCard
                    platform="Google Analytics"
                    onClick={() => handleConnectPlatform("Google Analytics")}
                  />
                )}
                {!isShopifyConnected && (
                  <ConnectPlatformCard
                    platform="Shopify"
                    onClick={() => handleConnectPlatform("Shopify")}
                  />
                )}
                {!isInstagramConnected && (
                  <ConnectPlatformCard
                    platform="Instagram"
                    onClick={() => handleConnectPlatform("Instagram")}
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {/* Performance Table */}
        <PerformanceTable
          performanceData={performanceData}
          apiStatus={apiStatus}
          onRefresh={fetchPerformanceData}
          loading={loading}
        />
        <ReportTable
          breakdownDim={breakdownDim}
          onBreakdownDimChange={setBreakdownDim}
          breakdown={breakdown}
          breakdownLoading={breakdownLoading}
          apiStatus={apiStatus}
          onRefresh={() => fetchBreakdownData(true)}
          loading={breakdownLoading}
        />

        <InstagramPerformanceTable
          brandId={brandId || ""}
          dateFrom={dateFrom ? dateFrom.slice(0, 10) : null}
          dateTo={dateTo ? dateTo.slice(0, 10) : null}
          isInstagramConnected={isInstagramConnected}
        />

        {/* Dashboard Quick Links Section */}
        <div className="mt-8">
          <div className="mb-6">
            <h2 className="text-xl font-bold text-slate-800">Quick Insights</h2>
            <p className="text-sm text-slate-500 mt-1">
              Click any card to explore detailed reports
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Conversion Funnel Card */}
            {brandId && (
              <ConversionFunnelCard
                onNavigate={() => navigate(`/ecommerce-reports/${brandId}`)}
                brandId={brandId}
                startDate={dateFrom ? dateFrom.slice(0, 10) : undefined}
                endDate={dateTo ? dateTo.slice(0, 10) : undefined}
              />
            )}

            {/* Marketing Insights Card - Monthly Metrics */}
            {brandId && (
              <MarketingInsightsCard
                onNavigate={() => navigate(`/marketing-insights/${brandId}`)}
                brandId={brandId}
                startDate={dateFrom ? dateFrom.slice(0, 10) : undefined}
                endDate={dateTo ? dateTo.slice(0, 10) : undefined}
              />
            )}

            {/* Payment Orders Card - COD vs Prepaid */}
            {brandId && (
              <PaymentOrdersCard
                onNavigate={() => navigate(`/reports/${brandId}`)}
                brandId={brandId}
                startDate={dateFrom ? dateFrom.slice(0, 10) : undefined}
                endDate={dateTo ? dateTo.slice(0, 10) : undefined}
              />
            )}
          </div>
        </div>
      </div>

      {/* Platform Connection Modal */}
      {selectedPlatform && brandId && (
        <PlatformModal
          platform={selectedPlatform}
          open={platformModalOpen}
          onOpenChange={setPlatformModalOpen}
          brandId={brandId}
          onSuccess={handlePlatformModalSuccess}
        />
      )}

      {/* Help Desk Modal */}
      {helpDeskModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <TicketForm onClose={() => setHelpDeskModalOpen(false)} />
          </div>
        </div>
      )}
    </div>
  );
};

export default SummaryDashboard;
